import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Select,
  HStack,
  Skeleton,
  SimpleGrid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  useDisclosure
} from '@chakra-ui/react'; import { Bar } from 'react-chartjs-2';
import { CategoryScale, Chart as ChartJS, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import OptionsTable from './OptionsTable';
import CaixaResumo from './CaixaResumo';
import { getJumba } from '../api/jumba';
import annotationPlugin from 'chartjs-plugin-annotation';
import debounce from 'lodash/debounce';
import { chamadaRemovida } from '../config/api';

// Registre os componentes Chart.js se ainda não estiverem registrados
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

const Jumba = ({ selectedStock, stockData, optionsValues, nearestDueDate, distinctDueDates = [], tickers = [], handleSelectStock: parentHandleSelectStock, tickersWithOptions = [], cliente = 0 }) => {
  const [optionsData, setOptionsData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(''); // Inicialmente vazio
  const [selectedOptionType, setSelectedOptionType] = useState('80');
  const [filteredData, setFilteredData] = useState([]);
  const [selectedCargaDate, setSelectedCargaDate] = useState('');
  const [lastPrice, setLastPrice] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalMessage, setModalMessage] = useState('');
  const [localSelectedStock, setLocalSelectedStock] = useState('');
  const [dueDates, setDueDates] = useState([]); // Estado para armazenar os vencimentos

  // Primeiro, ajuste a função formatDate para lidar melhor com diferentes formatos de data
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return ''; // Retorna vazio se a data for inválida
      
      const options = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        timeZone: 'UTC' // Importante para evitar problemas com timezone
      };
      return date.toLocaleDateString('pt-BR', options);
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return '';
    }
  };

  // Atualize a função handleLocalSelectStock para tratar melhor a resposta da API
  // Update handleLocalSelectStock to set selectedOptionType from the response data
const handleLocalSelectStock = async (ticker) => {
  console.log('Updating localSelectedStock to:', ticker);
  setLocalSelectedStock(ticker);

  try {
    const trimmedTicker = ticker.slice(0, 4).toLowerCase();
    const response = await getJumba(trimmedTicker);
    
    const validDates = response.vencimentos
      .filter(date => date && !isNaN(new Date(date).getTime()))
      .sort((a, b) => new Date(a) - new Date(b));

    console.log('Datas recebidas:', validDates);
    setDueDates(validDates);
    
    if (validDates.length > 0) {
      setSelectedDate(validDates[0]);
    } else {
      setSelectedDate('');
    }

    if (response.dados && response.dados.length > 0) {
      setOptionsData(response.dados);
      // Set selectedOptionType based on the first option's tMerc
      setSelectedOptionType(response.dados[0].tMerc);
    }
  } catch (error) {
    console.error('Erro ao buscar vencimentos:', error.message || error);
    setDueDates([]);
    setSelectedDate('');
    setOptionsData([]);
  }
};

  // Add this new useEffect at the top level of the component
  useEffect(() => {
    // Check if there are available tickers and none is currently selected
    if (tickersWithOptions.length > 0 && !localSelectedStock) {
      // Get the first enabled ticker
      const firstEnabledTicker = tickersWithOptions.find(ticker => 
        cliente === 1 || ['bova11', 'smal11', 'azul4', 'agro3', 'cbav3', 'mglu3', 'usim5']
          .includes(ticker.toLowerCase())
      );
  
      if (firstEnabledTicker) {
        // Call handleLocalSelectStock directly (which will fetch vencimentos)
        handleLocalSelectStock(firstEnabledTicker);
      }
    }
  }, [tickersWithOptions, cliente]); // Add dependencies

  const formatYAxis = (value) => {
    return value.toLocaleString('pt-BR'); // Formata o valor como string local
  };

  const [summaryData, setSummaryData] = useState({
    cobertas: { value: 0, percent: '0%' },
    travadas: { value: 0, percent: '0%' },
    descobertas: { value: 0, percent: '0%' },
    iqCalls: { value: 0, percent: '0%' },
    putCallRatio: { value: 0, percent: '0%' },
  });

  // Modify handleJumba to not require a date on first load
  const handleJumba = useCallback(async () => {
    try {
      if (!localSelectedStock) {
        console.warn('Ativo não selecionado.');
        return;
      }
  
      setOptionsData([]); // Clear previous data while loading
  
      const trimmedStock = localSelectedStock.slice(0, 4);
      console.log('Requesting data for:', trimmedStock, selectedDate);
  
      // Only include date in API call if it's selected
      const response = await getJumba(trimmedStock, selectedDate);
  
      if (!response.dados || response.dados.length === 0) {
        console.warn('Nenhum dado encontrado para o vencimento selecionado.');
        setOptionsData([]);
        return;
      }
  
      setOptionsData(response.dados);
    } catch (error) {
      console.error('Erro ao buscar dados do Jumba:', error.message || error);
      setOptionsData([]);
    }
  }, [selectedDate, localSelectedStock]);

  const debouncedHandleJumba = useMemo(
    () => debounce(handleJumba, 300),
    [handleJumba]
  );

  const handleSymbolChange = useCallback((symbol) => {
    handleLocalSelectStock(symbol);
    handleJumba(); // Call handleJumba directly instead of using debounced version
  }, [handleLocalSelectStock, handleJumba]);


  const handleTickerClick = (ticker, isEnabled) => {
    if (!isEnabled) {
      setModalMessage(
        `O ativo "${ticker}" está disponível apenas para assinantes do Fatcat Pro. Assine agora por R$ 47/mês e tenha acesso a tudo, com garantia de 14 dias.`
      );
      onOpen();
    }
  };

  useEffect(() => {

    if (!selectedDate && nearestDueDate) {
      setSelectedDate(nearestDueDate);
    }
    if (localSelectedStock && (selectedDate || nearestDueDate)) {
      handleJumba();
    }

    return () => debouncedHandleJumba.cancel();
  }, [localSelectedStock, selectedDate, nearestDueDate, handleJumba]);

  // Processamento de dados memoizado
  const addIQToOptions = useCallback((options) => {
    return options.map(option => {
      const iq = option.qtdClLan !== 0 ? (option.qtdClTit / option.qtdClLan).toFixed(2) : '-';
      return { ...option, iq: iq === '-' ? undefined : parseFloat(iq) };
    });
  }, []);

  const optionsWithIQ = useMemo(() => addIQToOptions(filteredData), [filteredData, addIQToOptions]);

  const filteredStrikesData = useMemo(() => {
    return optionsWithIQ
      .filter(option =>
        option.posTo > 0 &&
        Number(option.prEx) >= (lastPrice * 0.5) &&
        Number(option.prEx) <= (lastPrice * 1.5)
      )
      .sort((a, b) => Number(a.prEx) - Number(b.prEx));
  }, [optionsWithIQ, lastPrice]);

  // Configurações do gráfico memoizadas
  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => {
            const option = filteredStrikesData[context.dataIndex];
            return [
              `Opção: ${option.ser}`,
              `Strike: R$ ${Number(option.prEx).toFixed(2)}`,
              `Coberto: ${option.poCob.toLocaleString()}`,
              `Trava: ${option.posTr.toLocaleString()}`,
              `Descoberto: ${option.posDe.toLocaleString()}`,
              `Total: ${option.posTo.toLocaleString()}`,
              `IQ: ${option.iq}`,
            ];
          },
        },
      },
      annotation: {
        annotations: {
          referenceLine: {
            type: 'line',
            xMin: lastPrice,
            xMax: lastPrice,
            yMin: 0,
            yMax: 'max',
            borderColor: 'white',
            borderWidth: 2,
            label: {
              display: true,
              content: `R$ ${lastPrice.toFixed(2)}`,
              enabled: true,
              position: 'top',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              font: { size: 12, weight: 'bold' },
              padding: { top: 4, bottom: 4, left: 8, right: 8 },
              borderRadius: 4,
            }
          }
        }
      },
      legend: {
        position: 'top',
        labels: {
          color: '#FFFFFF'
        }
      }
    },
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: {
          display: true,
          text: 'Strike',
          color: '#FFFFFF'
        },
        ticks: {
          callback: value => parseFloat(value).toFixed(2),
          color: '#FFFFFF'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Quantidade',
          color: '#FFFFFF'
        },
        ticks: {
          callback: formatYAxis,
          color: '#FFFFFF'
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.1)'
        },
        stacked: true
      }
    },
    barPercentage: 0.9,
    categoryPercentage: 0.9,
    barThickness: 20,
    maxBarThickness: 25
  }), [lastPrice, filteredStrikesData, formatYAxis]);

  const chartData = useMemo(() => ({
    labels: filteredStrikesData.map(option => option.prEx),
    datasets: [
      {
        label: 'Descoberta',
        data: filteredStrikesData.map(option => option.posDe),
        backgroundColor: '#f56565',
        stack: 'stack1',
        barThickness: 'flex',
        maxBarThickness: 50
      },
      {
        label: 'Travada',
        data: filteredStrikesData.map(option => option.posTr),
        backgroundColor: '#ecc94b',
        stack: 'stack1',
        barThickness: 'flex',
        maxBarThickness: 50
      },
      {
        label: 'Coberta',
        data: filteredStrikesData.map(option => option.poCob),
        backgroundColor: '#4FD1C5',
        stack: 'stack1',
        barThickness: 'flex',
        maxBarThickness: 50
      }
    ]
  }), [filteredStrikesData]);

  // API calls
  const fetchSummaryFromAPI = useCallback(async (filteredData) => {
    if (filteredData.length === 0) return;

    const mer = filteredData[0]?.mer;
    if (!selectedDate || !mer) return;

    try {
      // Adiciona `selectedCargaDate` na URL da chamada à API
      const response = await chamadaRemovida();

      setLastPrice(parseFloat(response.data.price));

      setSummaryData({
        cobertas: {
          value: parseFloat(response.data.poCoberta),
          percent: parseFloat(response.data.var_poCob)
        },
        travadas: {
          value: parseFloat(response.data.poTravada),
          percent: parseFloat(response.data.var_posTr)
        },
        descobertas: {
          value: parseFloat(response.data.poDescoberta),
          percent: parseFloat(response.data.var_posDe)
        },
        iqCalls: {
          value: parseFloat(response.data.iq),
          percent: parseFloat(response.data.var_iq)
        },
        putCallRatio: {
          value: parseFloat(response.data.put_call_ratio),
          percent: parseFloat(response.data.var_put_call_ratio)
        }
      });
    } catch (error) {
      console.error('Erro ao buscar o resumo da API:', error);
    }
  }, [selectedDate, selectedOptionType, selectedCargaDate]);


  useEffect(() => {

    const cargaDates = Array.from(new Set(optionsData.map(option => option.dtCarga)));
    const mostRecentCargaDate = new Date(Math.max(...cargaDates
      .filter(date => date && !isNaN(new Date(date)))
      .map(date => new Date(date))
    ));

    if (!selectedCargaDate && mostRecentCargaDate && !isNaN(mostRecentCargaDate)) {
      setSelectedCargaDate(mostRecentCargaDate.toISOString().split('T')[0]);
    }

    const filtered = optionsData.filter(
      option =>
        (selectedDate ? option.dtVen === selectedDate : true) &&
        (selectedCargaDate ? option.dtCarga === selectedCargaDate : true) &&
        (selectedOptionType ? option.tMerc === selectedOptionType : true)
    );
    setFilteredData(filtered);
    fetchSummaryFromAPI(filtered);

  }, [selectedCargaDate, selectedOptionType, optionsData, selectedDate, fetchSummaryFromAPI]);

  const CustomSelect = ({ value, onChange, options, placeholder }) => (
    <select
      value={value}
      onChange={onChange}
      className="
        bg-[#1a1d24] 
        text-gray-100 
        text-sm
        px-3 
        py-1.5
        border
        border-gray-700
        rounded
        focus:outline-none
        focus:border-gray-600
        min-w-[120px]
        appearance-auto
      "
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map(option => (
        <option
          key={option.value}
          value={option.value}
          className={`
            bg-[#1a1d24]
            ${option.disabled ? 'text-gray-500' : 'text-gray-100'}
          `}
        >
          {option.label}
        </option>
      ))}
    </select>
  );

  return (
    <div className="w-full px-4 md:px-8 bg-gray-900 py-6">
<Box p={4}>
  <Box mb={4}>
    <Text fontSize="lg" color="white">Mapa das Opções B3</Text>
  </Box>

  <HStack spacing={2} mb={4}>
    <Select
      value={localSelectedStock || ''}
      onChange={(e) => {
        const selectedTicker = e.target.value;
        console.log('Selected ticker:', selectedTicker); // Debug log
        const isEnabled = cliente === 1 || ['bova11', 'smal11', 'azul4', 'agro3', 'cbav3', 'mglu3', 'usim5'].includes(selectedTicker.toLowerCase());
        if (isEnabled) {
          handleSymbolChange(selectedTicker);
        } else {
          handleTickerClick(selectedTicker, isEnabled);
        }
      }}
      bg="#1a1d24"
      borderColor="gray.700"
      color="white"
      size="sm"
      width="auto"
    >
      {tickersWithOptions
        .sort((a, b) => a.localeCompare(b))
        .map((ticker) => {
          const isEnabled = cliente === 1 || ['bova11', 'smal11', 'azul4', 'agro3', 'cbav3', 'mglu3', 'usim5'].includes(ticker.toLowerCase());
          return (
            <option
              key={ticker}
              value={ticker}
              style={{
                backgroundColor: '#1a1d24',
                color: isEnabled ? 'white' : 'gray',
                fontStyle: isEnabled ? 'normal' : 'italic',
              }}
            >
              {ticker}
            </option>
          );
        })}
    </Select>

    <Select
      value={selectedDate}
      onChange={(e) => {
        setSelectedDate(e.target.value);
        handleJumba(); // Chama o getJumba após selecionar o vencimento
      }}
      bg="#1a1d24"
      borderColor="gray.700"
      color="white"
      size="sm"
      width="auto"
      disabled={!dueDates.length} // Desabilita se não houver vencimentos
    >
      {dueDates.length > 0 ? (
        dueDates.map((date) => (
          <option key={date} value={date} style={{ backgroundColor: '#1a1d24' }}>
            {formatDate(date)}
          </option>
        ))
      ) : (
        <option value="">Selecione um vencimento</option>
      )}
    </Select>

    <Select
      value={selectedOptionType}
      onChange={(e) => setSelectedOptionType(e.target.value)}
      bg="#1a1d24"
      borderColor="gray.700"
      color="white"
      size="sm"
      width="auto"
    >
      <option value="70" style={{ backgroundColor: '#1a1d24' }}>CALL</option>
      <option value="80" style={{ backgroundColor: '#1a1d24' }}>PUT</option>
    </Select>

    <Select
      value={selectedCargaDate}
      onChange={(e) => setSelectedCargaDate(e.target.value)}
      bg="#1a1d24"
      borderColor="gray.700"
      color="white"
      size="sm"
      width="auto"
    >
      {Array.from(new Set(optionsData.map(option => option.dtCarga)))
        .sort((a, b) => new Date(b) - new Date(a))
        .map(date => (
          <option key={date} value={date} style={{ backgroundColor: '#1a1d24' }}>
            {formatDate(date)}
          </option>
        ))}
    </Select>
  </HStack>
  
  {/* Resto do componente ... */}
</Box>

        {filteredStrikesData.length > 0 ? (
          <Box height="500px" width="100%">
            <Bar
              data={chartData}
              options={chartOptions}
              redraw={false}
            />
          </Box>
        ) : (
          <Box width="100%" height="50vh" display="flex" flexDirection="column" justifyContent="center" alignItems="center" marginTop={1}>
            <Skeleton height="60px" width="100%" isLoaded={false} />
            <Box textAlign="center" color="white" mt={4}>
              Nenhum dado encontrado para o vencimento selecionado.
            </Box>
          </Box>
        )}


        <SimpleGrid columns={5} spacing={4} mt={6}>
          <CaixaResumo
            titulo="Cobertas"
            valor={summaryData.cobertas.value}
            percentual={summaryData.cobertas.percent}
            subtitulo="Total de CALLs"
            formatarComoInteiro={true}
            selectedOptionType={selectedOptionType}
          />
          <CaixaResumo
            titulo="Travadas"
            valor={summaryData.travadas.value}
            percentual={summaryData.travadas.percent}
            subtitulo="Total de CALLs"
            formatarComoInteiro={true}
            selectedOptionType={selectedOptionType}
          />
          <CaixaResumo
            titulo="Descobertas"
            valor={summaryData.descobertas.value}
            percentual={summaryData.descobertas.percent}
            subtitulo="Total de CALLs"
            formatarComoInteiro={true}
            selectedOptionType={selectedOptionType}
          />
          <CaixaResumo
            titulo="IQ das Calls"
            valor={summaryData.iqCalls.value}
            percentual={summaryData.iqCalls.percent}
            selectedOptionType={selectedOptionType}
          />
          <CaixaResumo
            titulo="PCR Ativo"
            valor={summaryData.putCallRatio.value}
            percentual={summaryData.putCallRatio.percent}
            selectedOptionType={selectedOptionType}
          />
        </SimpleGrid>

        {filteredStrikesData.length > 0 ? (
          <OptionsTable tableData={filteredStrikesData} optionsValues={optionsValues} />
        ) : (
          <Box textAlign="center" color="white" mt={4}>
            Nenhum dado encontrado para o vencimento selecionado.
          </Box>
        )}

        {/* Modal */}
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Acesso Restrito</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text>{modalMessage}</Text>
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="blue"
                onClick={() => {
                  window.open('https://pay.kiwify.com.br/qGNnjXV', '_blank');
                }}
              >
                Assinar Agora
              </Button>
              <Button variant="ghost" onClick={onClose} ml={3}>
                Fechar
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
      );
};

      export default React.memo(Jumba);